import { useCallback, useEffect, useRef, useState } from 'react';
import type { ApiClient } from '@center-technologies/shared-api-clients';
import type { StatusValue } from 'types/spherePoints';
import { useApiClientsOrNull } from '../context/ApiClientsContext';

const DEFAULT_LIMIT = 12;

export interface FactorHistoryCheckedInBy {
  sphereId: string;
  name: string;
  imageId?: string;
}

export interface FactorHistoryEntry {
  periodId: string;
  dueAt: string;
  status: StatusValue | 'missed' | 'skipped' | null;
  checkedInAt: string | null;
  checkedInBy?: FactorHistoryCheckedInBy;
  comment?: string;
}

export interface UseFactorHistoryOptions {
  factorId: string | undefined;
  limit?: number;
  enabled?: boolean;
}

export interface UseFactorHistoryResult {
  history: FactorHistoryEntry[];
  cadence: string | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

interface InFlightEntry {
  promise: Promise<FetchResult>;
  controller: AbortController;
}

interface FetchResult {
  history: FactorHistoryEntry[];
  cadence: string | null;
}

const EMPTY_HISTORY: FactorHistoryEntry[] = [];

const inFlightCache = new Map<string, InFlightEntry>();

function buildCacheKey(factorId: string, limit: number): string {
  return `${factorId}:${limit}`;
}

function checkedInByEqual(
  a: FactorHistoryCheckedInBy | undefined,
  b: FactorHistoryCheckedInBy | undefined
): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.sphereId === b.sphereId &&
    a.name === b.name &&
    a.imageId === b.imageId
  );
}

function arraysEqual(
  a: FactorHistoryEntry[],
  b: FactorHistoryEntry[]
): boolean {
  if (a === b) return true;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    const entryA = a[i];
    const entryB = b[i];
    if (
      entryA.periodId !== entryB.periodId ||
      entryA.dueAt !== entryB.dueAt ||
      entryA.status !== entryB.status ||
      entryA.checkedInAt !== entryB.checkedInAt ||
      entryA.comment !== entryB.comment ||
      !checkedInByEqual(entryA.checkedInBy, entryB.checkedInBy)
    ) {
      return false;
    }
  }
  return true;
}

function parseCheckedInBy(raw: unknown): FactorHistoryCheckedInBy | undefined {
  if (!raw || typeof raw !== 'object') return undefined;
  const obj = raw as Record<string, unknown>;
  if (typeof obj.sphereId !== 'string' || typeof obj.name !== 'string') {
    return undefined;
  }
  const result: FactorHistoryCheckedInBy = {
    sphereId: obj.sphereId,
    name: obj.name,
  };
  if (typeof obj.imageId === 'string') {
    result.imageId = obj.imageId;
  }
  return result;
}

function parseEntry(raw: unknown): FactorHistoryEntry | null {
  if (!raw || typeof raw !== 'object') return null;
  const obj = raw as Record<string, unknown>;
  if (typeof obj.periodId !== 'string' || typeof obj.dueAt !== 'string') {
    return null;
  }
  const entry: FactorHistoryEntry = {
    periodId: obj.periodId,
    dueAt: obj.dueAt,
    status: (obj.status ?? null) as FactorHistoryEntry['status'],
    checkedInAt:
      typeof obj.checkedInAt === 'string' ? obj.checkedInAt : null,
  };
  const checkedInBy = parseCheckedInBy(obj.checkedInBy);
  if (checkedInBy) {
    entry.checkedInBy = checkedInBy;
  }
  if (typeof obj.comment === 'string') {
    entry.comment = obj.comment;
  }
  return entry;
}

function parseResponse(raw: unknown): FetchResult {
  if (!raw || typeof raw !== 'object') {
    return { history: [], cadence: null };
  }
  const obj = raw as Record<string, unknown>;
  const history = Array.isArray(obj.history)
    ? obj.history
        .map(parseEntry)
        .filter((entry): entry is FactorHistoryEntry => entry !== null)
    : [];
  const cadence =
    typeof obj.cadence === 'string' ? obj.cadence : null;
  return { history, cadence };
}

async function performFetch(
  graphClient: ApiClient,
  factorId: string,
  limit: number,
  controller: AbortController
): Promise<FetchResult> {
  const raw = await graphClient.get<unknown>(
    `/factors/${encodeURIComponent(factorId)}/history?limit=${limit}`,
    { signal: controller.signal }
  );
  return parseResponse(raw);
}

/**
 * Fetch recent check-in period statuses for a manual status factor.
 * Used by the task list sparkline column and the check-in dialog's recent-history panel.
 *
 * Shares one in-flight fetch across all subscribers of the same `factorId:limit`
 * so a grid with N rows pointing at the same factor only triggers one HTTP call.
 * The cache entry is released once the fetch settles; it is not a long-lived data cache.
 */
export function useFactorHistory({
  factorId,
  limit = DEFAULT_LIMIT,
  enabled = true,
}: UseFactorHistoryOptions): UseFactorHistoryResult {
  const apiClients = useApiClientsOrNull();
  const graphClient: ApiClient | undefined = apiClients?.graphClient;

  const [history, setHistory] = useState<FactorHistoryEntry[]>(EMPTY_HISTORY);
  const [cadence, setCadence] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const historyRef = useRef<FactorHistoryEntry[]>(EMPTY_HISTORY);
  const latestRequestIdRef = useRef(0);
  const bypassCacheRef = useRef(false);

  const runFetch = useCallback(() => {
    if (!enabled || !factorId || !graphClient) {
      return;
    }

    const requestId = ++latestRequestIdRef.current;
    const key = buildCacheKey(factorId, limit);

    let entry = inFlightCache.get(key);
    if (!entry || bypassCacheRef.current) {
      if (entry && bypassCacheRef.current) {
        entry.controller.abort();
        inFlightCache.delete(key);
      }
      const controller = new AbortController();
      const promise = performFetch(graphClient, factorId, limit, controller)
        .finally(() => {
          if (inFlightCache.get(key)?.controller === controller) {
            inFlightCache.delete(key);
          }
        });
      entry = { promise, controller };
      inFlightCache.set(key, entry);
    }
    bypassCacheRef.current = false;

    setLoading(true);
    setError(null);

    entry.promise
      .then((result) => {
        if (requestId !== latestRequestIdRef.current) return;
        setHistory((prev) =>
          arraysEqual(prev, result.history) ? prev : result.history
        );
        historyRef.current = result.history;
        setCadence(result.cadence);
        setLoading(false);
      })
      .catch((err: unknown) => {
        if (requestId !== latestRequestIdRef.current) return;
        if (err instanceof DOMException && err.name === 'AbortError') {
          return;
        }
        setError('taskList.error.historyFetchFailed');
        setLoading(false);
      });
  }, [enabled, factorId, graphClient, limit]);

  useEffect(() => {
    if (!enabled || !factorId || !graphClient) {
      latestRequestIdRef.current++;
      setHistory(EMPTY_HISTORY);
      historyRef.current = EMPTY_HISTORY;
      setCadence(null);
      setLoading(false);
      setError(null);
      return;
    }
    runFetch();
  }, [enabled, factorId, graphClient, limit, runFetch]);

  useEffect(() => {
    return () => {
      latestRequestIdRef.current++;
    };
  }, []);

  const refetch = useCallback(() => {
    bypassCacheRef.current = true;
    runFetch();
  }, [runFetch]);

  return { history, cadence, loading, error, refetch };
}

/**
 * Exposed for tests only. Do not use from application code.
 */
export function __resetFactorHistoryInFlightCacheForTests(): void {
  inFlightCache.forEach((entry) => entry.controller.abort());
  inFlightCache.clear();
}
